import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { SummaryReportSection as Section, SummaryReportTable as KvTable } from './components/SummaryReport';
import { JsonTree } from './components/JsonTree';
import { getPackageSummary } from './hooks/usePackages';
import { toTestIdPart } from './utils/testIds';

// ── Types ──────────────────────────────────────────────────────────────────────

interface PackageInfo {
  id: number;
  facilityId: number;
  productId: number;
  tagId: number;
  label: string;
  packageType?: string;
  productionBatchNumber?: string;
  productName?: string;
  strain?: string;
  productCategoryName?: string;
  sourcePackageLabels?: string;
  labTestingStateName?: string;
  labTestingStateDate?: string;
  packagedDate?: string;
  quantity?: number;
  unitOfMeasureId?: number;
  unitOfMeasureName?: string;
  retailIdQrCount?: number;
  labelsLastGeneratedDateTime?: string;
  importedAt?: string;
  marketCode?: string;
  isOnRecall: boolean;
  lastModified?: string;
}

interface CoaHistoryItem {
  id: number;
  packageId: number;
  created: string;
  data: string;
  coaFilesIds?: number[];
  coaFiles?: string[];
  coaFileNames?: string[];
}

interface PrintRunItem {
  id: number;
  jobName?: string;
  jobId?: string;
  status: number;
  templateId: number;
  templateName?: string;
  packageLabel?: string;
  count: number;
  productName?: string;
  packagedDate?: string;
  printedDate?: string;
  createdAt: string;
}

interface FacilitySummary {
  id: number;
  name?: string;
  license?: string;
}

interface OrganizationSummary {
  id: number;
  name?: string;
}

interface PackageSummaryResult {
  package: PackageInfo;
  organization?: OrganizationSummary;
  facility?: FacilitySummary;
  coaHistory: CoaHistoryItem[];
  printRuns: PrintRunItem[];
  coaFiles?: string[];
  coaFileNames?: string[];
}

// ── Constants ──────────────────────────────────────────────────────────────────

const PRINT_STATUS: Record<number, string> = {
  0: 'Queued',
  1: 'In Progress',
  2: 'Completed',
  3: 'Failed',
};

const PRINT_STATUS_COLOR: Record<number, { bg: string; text: string }> = {
  0: { bg: '#f1f3f4', text: '#666' },
  1: { bg: '#fff3e0', text: '#b45309' },
  2: { bg: '#e6f4ea', text: '#22863a' },
  3: { bg: '#fde8e8', text: '#cb2431' },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(val: string | null | undefined): string {
  if (!val) return '';
  try {
    return new Date(val).toLocaleString();
  } catch {
    return val;
  }
}

function hasDisplayValue(val: unknown): boolean {
  if (val === null || val === undefined || val === '') return false;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') return Object.keys(val as object).length > 0;
  return true;
}

// The summary API returns only file URLs and names, so the name (or URL path) is the stable identity.
function getCoaFileTestId(fileName: string | undefined, url: string): string {
  const source = fileName || (() => {
    try {
      return new URL(url).pathname.split('/').pop() || url;
    } catch {
      return url;
    }
  })();
  return `package-summary-coa-file-${toTestIdPart(source)}`;
}

function pruneEmptyValue(val: unknown): unknown {
  if (val === null || val === undefined || val === '') return null;

  if (Array.isArray(val)) {
    const pruned = val
      .map((item) => pruneEmptyValue(item))
      .filter((item) => item !== null);
    return pruned.length > 0 ? pruned : null;
  }

  if (typeof val === 'object') {
    const entries = Object.entries(val as Record<string, unknown>)
      .map(([k, v]) => [k, pruneEmptyValue(v)] as const)
      .filter(([, v]) => v !== null);

    if (entries.length === 0) return null;
    return Object.fromEntries(entries);
  }

  return val;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function CoaItem({ coa, index }: { coa: CoaHistoryItem; index: number }) {
  const [open, setOpen] = useState(false);
  let parsedData: unknown = coa.data;

  while (typeof parsedData === 'string') {
    try {
      parsedData = JSON.parse(parsedData);
    } catch {
      break;
    }
  }

  const prunedCoaData = pruneEmptyValue(parsedData);
  const hasCoaData = hasDisplayValue(prunedCoaData);

  return (
    <div
      data-testid={`package-summary-coa-${coa.id}`}
      style={{
        border: '1px solid #e4e8ec',
        borderRadius: '4px',
        marginTop: '0.5rem',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} node COA #${index + 1}`}
        data-testid={`package-summary-coa-${coa.id}-toggle`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          width: '100%',
          background: open ? '#f0f4f8' : 'white',
          border: 'none',
          cursor: 'pointer',
          padding: '0.55rem 0.75rem',
          fontWeight: 500,
          fontSize: '0.83rem',
          textAlign: 'left',
          color: '#333',
        }}
      >
        <span style={{ fontSize: '0.6rem', color: 'var(--brand)' }}>
          {open ? '▼' : '▶'}
        </span>
        <span style={{ flex: 1 }}>
          COA #{index + 1}
          {coa.created && (
            <span style={{ fontWeight: 400, color: '#999', marginLeft: '0.5rem' }}>
              — {fmtDate(coa.created)}
            </span>
          )}
        </span>
      </button>

      {open && (
        <div
          data-testid={`package-summary-coa-${coa.id}-content`}
          style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e4e8ec', background: '#fafafa' }}
        >
          <div style={{ marginBottom: '0.5rem', fontSize: '0.72rem', color: '#aaa' }}>
            ID: {coa.id} · Package ID: {coa.packageId}
          </div>

          {hasCoaData && (
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: '#555',
                  marginBottom: '0.4rem',
                }}
              >
                COA Data
              </div>
              <div
                style={{
                  background: 'white',
                  border: '1px solid #e4e8ec',
                  borderRadius: '4px',
                  padding: '0.75rem',
                }}
              >
                <JsonTree value={prunedCoaData} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PrintRunsTable({ printRuns }: { printRuns: PrintRunItem[] }) {
  if (printRuns.length === 0) {
    return (
      <p className="muted" style={{ padding: '0.5rem 0.25rem', fontSize: '0.83rem' }} data-testid="package-summary-print-runs-empty">
        No print runs.
      </p>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table" style={{ fontSize: '0.82rem' }} data-testid="package-summary-print-runs-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Job Name</th>
            <th>Template</th>
            <th>Status</th>
            <th>Count</th>
            <th>Printed</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {printRuns.map((p, i) => {
            const colors = PRINT_STATUS_COLOR[p.status] ?? PRINT_STATUS_COLOR[0];
            return (
              <tr key={p.id} style={{ background: i % 2 === 0 ? 'white' : '#f8f9fa' }} data-testid={`package-summary-print-run-${p.id}`}>
                <td style={{ fontFamily: 'monospace', color: '#888' }}>{p.id}</td>
                <td>{p.jobName || '—'}</td>
                <td>{p.templateName || p.templateId}</td>
                <td>
                  <span className="badge" style={{ background: colors.bg, color: colors.text }}>
                    {PRINT_STATUS[p.status] ?? p.status}
                  </span>
                </td>
                <td>{p.count}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.printedDate) || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PackageResultCard({ result }: { result: PackageSummaryResult }) {
  const { package: pkg, organization, facility, coaHistory, printRuns } = result;
  // Compose and filter package info rows
  const packageInfoRows: Array<[string, unknown]> = [
    ['ID', pkg.id],
    ['Package Type', pkg.packageType],
    ['Strain', pkg.strain],
    [
      'Quantity',
      pkg.quantity != null
        ? `${pkg.quantity}${pkg.unitOfMeasureName ? ' ' + pkg.unitOfMeasureName : ''}`
        : null,
    ],
    ['Packaged Date', fmtDate(pkg.packagedDate)],
    ['Lab Testing State', pkg.labTestingStateName],
    ['Lab Testing Date', fmtDate(pkg.labTestingStateDate)],
    ['Market', pkg.marketCode],
    ['On Recall', pkg.isOnRecall ? 'Yes' : null],
    ['Batch Number', pkg.productionBatchNumber],
    ['Source Package Labels', pkg.sourcePackageLabels],
    ['QR Count', pkg.retailIdQrCount],
    ['Labels Last Generated', fmtDate(pkg.labelsLastGeneratedDateTime)],
    ['Imported At', fmtDate(pkg.importedAt)],
    ['Last Modified', fmtDate(pkg.lastModified)],
    ['Tag ID', pkg.tagId],
    ['Facility ID', pkg.facilityId],
    ['Product ID', pkg.productId],
  ];
  const filteredPackageInfoRows = packageInfoRows.filter(([, v]) => hasDisplayValue(v));

  const organizationRows: Array<[string, unknown]> = [
    ['ID', organization?.id],
    ['Name', organization?.name],
  ];
  const filteredOrganizationRows = organizationRows.filter(([, v]) => hasDisplayValue(v));
  const organizationLabel = organization
    ? `Org: ${organization.name || 'Unknown'} (ID ${organization.id})`
    : null;

  const facilityRows: Array<[string, unknown]> = [
    ['ID', facility?.id],
    ['Name', facility?.name],
    ['License', facility?.license],
  ];
  const filteredFacilityRows = facilityRows.filter(([, v]) => hasDisplayValue(v));

  return (
    <div className="card" style={{ padding: '1.5rem' }} data-testid={`package-summary-result-${pkg.id}`}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: '1rem',
              fontFamily: 'monospace',
              letterSpacing: '0.02em',
            }}
          >
            {pkg.label}
          </div>
          {pkg.productName && (
            <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '0.15rem' }}>
              {pkg.productName}
              {pkg.productCategoryName && (
                <span style={{ marginLeft: '0.5rem', color: '#aaa' }}>
                  · {pkg.productCategoryName}
                </span>
              )}
            </div>
          )}
        </div>
        {organizationLabel && (
          <span className="badge" style={{ background: '#eef6ff', color: '#075985', fontWeight: 700 }}>
            {organizationLabel}
          </span>
        )}
        {pkg.marketCode && <span className="badge">{pkg.marketCode}</span>}
        {pkg.isOnRecall && (
          <span className="badge" style={{ background: '#fde8e8', color: '#cb2431' }}>
            ⚠ On Recall
          </span>
        )}
      </div>

      {filteredPackageInfoRows.length > 0 && (
        <Section title="Package Info" defaultOpen={true} testId="package-summary-package-info">
          <KvTable rows={filteredPackageInfoRows} />
        </Section>
      )}

      {organization && filteredOrganizationRows.length > 0 && (
        <Section title="Organization" defaultOpen={true} testId="package-summary-organization">
          <KvTable rows={filteredOrganizationRows} />
        </Section>
      )}

      {facility && filteredFacilityRows.length > 0 && (
        <Section title="Facility" defaultOpen={true} testId="package-summary-facility">
          <KvTable rows={filteredFacilityRows} />
        </Section>
      )}

      {result.coaFiles && result.coaFiles.length > 0 && (
        <Section title="COA Files" count={result.coaFiles.length} defaultOpen={true} testId="package-summary-coa-files">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', padding: '0.25rem 0.1rem' }}>
            {result.coaFiles.map((url, i) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.83rem', color: 'var(--brand)' }}
                data-testid={getCoaFileTestId(result.coaFileNames?.[i], url)}
              >
                {result.coaFileNames?.[i] || url}
              </a>
            ))}
          </div>
        </Section>
      )}

      {coaHistory.length > 0 && (
        <Section title="COA History" count={coaHistory.length} defaultOpen={true} testId="package-summary-coa-history">
          {coaHistory.map((coa, i) => <CoaItem key={coa.id} coa={coa} index={i} />)}
        </Section>
      )}

      {printRuns.length > 0 && (
        <Section title="Print Runs" count={printRuns.length} defaultOpen={true} testId="package-summary-print-runs">
          <PrintRunsTable printRuns={printRuns} />
        </Section>
      )}
    </div>
  );
}

function ViewToggle({
  mode,
  onChange,
}: {
  mode: 'structured' | 'raw';
  onChange: (m: 'structured' | 'raw') => void;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <button
        type="button"
        className={`button${mode === 'structured' ? ' cta' : ''}`}
        onClick={() => onChange('structured')}
        data-testid="package-summary-view-structured"
      >
        Structured
      </button>
      <button
        type="button"
        className={`button${mode === 'raw' ? ' cta' : ''}`}
        onClick={() => onChange('raw')}
        data-testid="package-summary-view-raw"
      >
        Raw JSON
      </button>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function PackageSummaryPage() {
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [result, setResult] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [viewMode, setViewMode] = useState<'structured' | 'raw'>('structured');

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setNotFound(false);

    try {
      const data = await getPackageSummary(trimmed);
      setResult(data);
    } catch (err: unknown) {
      if ((err as any)?.response?.status === 404 || (err as any)?.status === 404) {
        setNotFound(true);
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const results = Array.isArray(result) ? (result as PackageSummaryResult[]) : null;
  const hasRenderableItems = !results || results.length > 0;

  return (
    <div className="stack" data-testid="page-package-summary">
      <PageHeader
        title="Package Summary"
        subtitle="Look up a package summary by label"
        actions={
          <button type="button" className="button" onClick={() => navigate('/internal')} data-testid="package-summary-back">
            Back
          </button>
        }
      />

      <div className="card" style={{ padding: '2rem' }}>
        <form className="stack" onSubmit={handleSearch}>
          <label htmlFor="package-label-input" style={{ fontWeight: 600 }}>
            Package Label
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              id="package-label-input"
              type="text"
              className="input"
              placeholder="Enter package label…"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={loading}
              style={{ flex: 1 }}
              data-testid="package-label-input"
            />
            <button
              type="submit"
              className="button cta"
              disabled={loading || !label.trim()}
              data-testid="package-summary-search-btn"
            >
              {loading ? 'Searching…' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="card" style={{ padding: '2rem' }}>
          <p style={{ color: 'var(--error, red)' }} data-testid="package-summary-error">
            {error}
          </p>
        </div>
      )}

      {(notFound || (result !== null && Array.isArray(result) && result.length === 0)) && (
        <div
          className="card"
          style={{ padding: '1.5rem', textAlign: 'center', color: '#888', fontSize: '0.9rem' }}
          data-testid="package-summary-no-results"
        >
          No results found for label <strong>{label}</strong>.
        </div>
      )}

      {result !== null && hasRenderableItems && (
        <div className="stack">
          <ViewToggle mode={viewMode} onChange={setViewMode} />

          {viewMode === 'structured' ? (
            results ? (
              <>
                {results.length > 1 && (
                  <div
                    className="card"
                    style={{ padding: '0.75rem 1.25rem', fontSize: '0.85rem', color: '#666' }}
                    data-testid="package-summary-result-count"
                  >
                    {results.length} results found for label <strong>{label}</strong>
                  </div>
                )}
                {results.map((r, i) => (
                  <PackageResultCard key={i} result={r} />
                ))}
              </>
            ) : (
              <div className="card" style={{ padding: '1.5rem' }} data-testid="package-summary-structured-output">
                <JsonTree value={result} />
              </div>
            )
          ) : (
            <div className="card" style={{ padding: '2rem' }}>
              <textarea
                id="package-summary-output"
                readOnly
                value={JSON.stringify(result, null, 2)}
                rows={24}
                style={{
                  width: '100%',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  resize: 'vertical',
                }}
                data-testid="package-summary-output"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
